import { ChangeEvent, FocusEvent } from "react";
import { FieldError, UseFormRegisterReturn } from "react-hook-form";

export type InputError = FieldError | string;

export function getErrorMessage(error?: InputError): string | undefined {
  if (!error) return undefined;
  if (typeof error === "string") return error;
  return error.message;
}

/**
 * Props shared by all form inputs.
 * Supports two modes:
 * - **react-hook-form**: pass `registration` (from `register(name)`)
 * - **controlled**: pass `value` and `onChange`
 */
export type BaseInputProps = {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: InputError;
  /** The register return value from react-hook-form `register(name)`. */
  registration?: UseFormRegisterReturn;
  /** Controlled value (used when not using react-hook-form). */
  value?: string;
  /** Controlled onChange handler (used when not using react-hook-form). */
  onChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  onFocus?: (
    e: FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  showErrors?: boolean;
};

export type TextInputProps = BaseInputProps & {
  autoComplete?: "on" | "off";
  maxLength?: number;
  placeholder?: string;
};

export type DateTimeInputProps = BaseInputProps & {
  /** Whether to show a default value (current date/time). Defaults to true. */
  showDefaultValue?: boolean;
  min?: string;
  max?: string;
};

export type SelectInputProps = BaseInputProps & {
  options?: { id: string; name: string }[];
  placeholder?: string;
  hint?: string;
};

/** Props for multi-select inputs (Controller-based). */
export type MultiSelectInputProps = {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: InputError;
  options?: string[];
  value?: string[];
  onChange: (value: string[]) => void;
};

export type RadioInputProps = BaseInputProps & {
  options?: { label: string; value: string | number | boolean }[];
};

/** Props for toggle inputs (Controller-based). */
export type ToggleInputProps = {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: InputError;
  options?: {
    label: string;
    value: string | number | boolean;
    /** Optional icon name rendered before the label (e.g. "Lock_closed"). */
    icon?: string;
  }[];
  value: string | number | boolean | null;
  onChange: (value: string | number | boolean) => void;
  /** Optional helper text rendered below the toggle, aligned with its options. */
  warningText?: string;
};

export type AttachmentsInputProps = {
  attachments: File[];
  disabled?: boolean;
  error?: InputError;
  onRemoveFile: (name: string) => void;
  /** Callback when files are submitted from the attach dialog. */
  onFilesChange: (files: File[]) => void;
  /** Optional MIME-type / extension filter forwarded to the file picker (e.g. "application/pdf"). */
  accept?: string;
  /** Optional cap on number of files retained after selection. */
  maxFiles?: number;
  /** Override for the "Attach File" button label. */
  buttonText?: string;
};
